import CompanyLayout from '@/components/feature/CompanyLayout';
import { useTheme } from '@/contexts/ThemeContext';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';

interface JobPosting {
  id: string;
  title: string;
  department: string;
  location: string;
  type: string;
  description: string;
  requirements: string[];
}

const departmentKeys = [
  { key: 'All', label: 'All Departments' },
  { key: 'Engineering', label: 'Engineering' },
  { key: 'AI & Research', label: 'AI & Research' },
  { key: 'Product', label: 'Product' },
  { key: 'Design', label: 'Design' },
  { key: 'Operations', label: 'Operations' },
  { key: 'Legal', label: 'Legal' },
];

const jobs: JobPosting[] = [
  {
    id: 'senior-rust-engineer',
    title: 'Senior Rust Engineer',
    department: 'Engineering',
    location: 'Remote / Singapore',
    type: 'Full-time',
    description: 'Lead the development of high-performance on-chain settlement infrastructure. You will architect and build core trading engine components that process millions of transactions per second.',
    requirements: [
      '5+ years of experience in systems programming',
      'Expert-level proficiency in Rust',
      'Deep understanding of consensus mechanisms and blockchain architecture',
      'Experience with low-latency, high-throughput distributed systems',
      'Familiarity with Solana, Ethereum, or other L1/L2 protocols',
    ],
  },
  {
    id: 'ai-research-scientist',
    title: 'AI Research Scientist',
    department: 'AI & Research',
    location: 'Remote / San Francisco',
    type: 'Full-time',
    description: 'Research and develop novel machine learning models for market prediction, optimal execution, and risk management. Push the boundaries of AI in decentralized finance.',
    requirements: [
      'PhD or equivalent experience in Machine Learning, Statistics, or related field',
      'Published research in top-tier venues (NeurIPS, ICML, ICLR)',
      'Strong programming skills in Python and PyTorch/TensorFlow',
      'Experience with time-series forecasting and reinforcement learning',
      'Interest in financial markets and trading strategies',
    ],
  },
  {
    id: 'protocol-security-engineer',
    title: 'Protocol Security Engineer',
    department: 'Engineering',
    location: 'Remote',
    type: 'Full-time',
    description: 'Identify and mitigate security vulnerabilities across our smart contracts, APIs, and infrastructure. Lead our bug bounty program and conduct regular security audits.',
    requirements: [
      '3+ years in blockchain security or smart contract auditing',
      'Proven track record of discovering critical vulnerabilities',
      'Experience with formal verification tools (Certora, Coq, etc.)',
      'Strong understanding of DeFi attack vectors (reentrancy, flash loans, oracle manipulation)',
      'OSCP or equivalent security certification preferred',
    ],
  },
  {
    id: 'product-manager-trading',
    title: 'Product Manager - Trading',
    department: 'Product',
    location: 'Remote / Dubai',
    type: 'Full-time',
    description: 'Define the roadmap for our core trading products. Work closely with engineering, design, and quantitative research teams to deliver industry-leading trading experiences.',
    requirements: [
      '4+ years of product management experience in fintech or crypto',
      'Deep understanding of derivatives trading, order management, and execution systems',
      'Data-driven decision making with strong SQL and analytics skills',
      'Experience working with engineering teams in agile environments',
      'Prior trading experience or CFA qualification is a plus',
    ],
  },
  {
    id: 'ui-ux-designer',
    title: 'Senior UI/UX Designer',
    department: 'Design',
    location: 'Remote / Tokyo',
    type: 'Full-time',
    description: 'Craft intuitive and visually stunning interfaces for our trading platform. Balance the complexity of financial instruments with the simplicity of consumer-grade design.',
    requirements: [
      '5+ years of UI/UX design experience',
      'Exceptional portfolio demonstrating complex data visualization work',
      'Proficiency in Figma and prototyping tools',
      'Experience designing for financial or trading applications',
      'Understanding of accessibility standards (WCAG 2.1 AA)',
    ],
  },
  {
    id: 'quantitative-trader',
    title: 'Quantitative Trader',
    department: 'AI & Research',
    location: 'Remote / London',
    type: 'Full-time',
    description: 'Develop and deploy quantitative trading strategies across our platform. Build alpha models, execution algorithms, and risk management frameworks for institutional-grade trading.',
    requirements: [
      '3+ years in quantitative trading or research at a hedge fund or prop firm',
      'Strong programming skills in Python and C++',
      'Experience with statistical arbitrage, market making, or momentum strategies',
      'Proficiency in portfolio optimization and risk modeling',
      'Masters or PhD in a quantitative discipline preferred',
    ],
  },
  {
    id: 'devrel-engineer',
    title: 'Developer Relations Engineer',
    department: 'Operations',
    location: 'Remote',
    type: 'Full-time',
    description: 'Build and nurture our developer ecosystem. Create documentation, SDKs, and sample applications that make integrating with ZhiSu Protocol seamless for external developers.',
    requirements: [
      '3+ years in developer relations, technical writing, or solutions engineering',
      'Strong coding skills in TypeScript, Python, or Rust',
      'Experience with API documentation and SDK development',
      'Active presence in developer communities (Discord, Twitter, GitHub)',
      'Excellent communication and presentation skills',
    ],
  },
  {
    id: 'compliance-officer',
    title: 'Compliance Officer',
    department: 'Legal',
    location: 'Singapore / BVI',
    type: 'Full-time',
    description: 'Lead our global compliance program, ensuring adherence to AML/KYC regulations across all jurisdictions. Develop policies, conduct investigations, and interface with regulators.',
    requirements: [
      '5+ years in compliance within financial services or fintech',
      'In-depth knowledge of FATF guidelines, EU AML directives, and US FinCEN regulations',
      'Experience with blockchain analytics tools (Chainalysis, Elliptic, TRM Labs)',
      'CAMS or equivalent certification required',
      'Juris Doctor or equivalent legal qualification preferred',
    ],
  },
];

function JobCard({ job, isExpanded, onToggle }: { job: JobPosting; isExpanded: boolean; onToggle: () => void }) {
  const { isDark } = useTheme();
  const textPrimary = isDark ? 'text-white' : 'text-[#111]';
  const textSecondary = isDark ? 'text-[#888]' : 'text-[#666]';
  const textMuted = isDark ? 'text-[#555]' : 'text-[#999]';
  const borderLight = isDark ? 'border-white/[0.06]' : 'border-black/[0.06]';

  return (
    <div className={`rounded-xl border ${borderLight} overflow-hidden transition-all`}>
      <button
        onClick={onToggle}
        className={`w-full p-5 flex items-start justify-between gap-4 text-left hover:bg-white/[0.02] transition-colors`}
      >
        <div className="flex-1 min-w-0">
          <h3 className={`font-semibold ${textPrimary}`}>{job.title}</h3>
          <div className={`flex flex-wrap items-center gap-3 mt-2 text-xs ${textMuted}`}>
            <span className="flex items-center gap-1">
              <i className="ri-building-line w-3 h-3 flex items-center justify-center" />
              {job.department}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-map-pin-line w-3 h-3 flex items-center justify-center" />
              {job.location}
            </span>
            <span className="px-2 py-0.5 rounded-full bg-[#00ff88]/10 text-[#00ff88] font-medium">
              {job.type}
            </span>
          </div>
        </div>
        <i
          className={`ri-arrow-down-s-line w-5 h-5 flex items-center justify-center flex-shrink-0 ${textMuted} transition-transform ${isExpanded ? 'rotate-180' : ''}`}
        />
      </button>
      {isExpanded && (
        <div className={`px-5 pb-5 border-t ${borderLight}`}>
          <p className={`mt-4 text-sm leading-relaxed ${textSecondary}`}>{job.description}</p>
          <h4 className={`mt-4 mb-2 text-sm font-semibold ${textPrimary}`}>Requirements</h4>
          <ul className="space-y-2">
            {job.requirements.map((req, i) => (
              <li key={i} className={`flex items-start gap-2 text-sm ${textSecondary}`}>
                <i className="ri-check-line text-[#00ff88] w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5" />
                {req}
              </li>
            ))}
          </ul>
          <a
            href="mailto:careers@zhisu.trade"
            className="inline-flex items-center gap-2 mt-5 px-4 py-2 rounded-lg bg-[#00ff88]/10 text-[#00ff88] text-sm font-medium hover:bg-[#00ff88]/20 transition-colors"
          >
            Apply Now
            <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center" />
          </a>
        </div>
      )}
    </div>
  );
}

export default function CareersPage() {
  const { isDark } = useTheme();
  const { t } = useTranslation();
  const textPrimary = isDark ? 'text-white' : 'text-[#111]';
  const textSecondary = isDark ? 'text-[#888]' : 'text-[#666]';
  const textMuted = isDark ? 'text-[#555]' : 'text-[#999]';
  const bgCard = isDark ? 'bg-[#0a0a0a]' : 'bg-white';
  const borderLight = isDark ? 'border-white/[0.06]' : 'border-black/[0.06]';

  const [activeDept, setActiveDept] = useState('All');
  const [expandedJob, setExpandedJob] = useState<string | null>(null);

  const filteredJobs = activeDept === 'All'
    ? jobs
    : jobs.filter((j) => j.department === activeDept);

  const benefits = [
    { icon: 'ri-earth-line', titleKey: 'pages.careers.benefit2Title', title: 'Remote First', descKey: 'pages.careers.benefit2Desc', desc: 'Work from anywhere in the world' },
    { icon: 'ri-coins-line', titleKey: 'pages.careers.benefit1Title', title: 'Competitive Compensation', descKey: 'pages.careers.benefit1Desc', desc: 'Above-market salaries, token allocations, and performance bonuses' },
    { icon: 'ri-heart-pulse-line', titleKey: 'pages.careers.benefit3Title', title: 'Cutting Edge Tech', descKey: 'pages.careers.benefit3Desc', desc: 'Build with the latest in AI, blockchain, and distributed systems' },
    { icon: 'ri-calendar-check-line', titleKey: 'pages.careers.benefit4Title', title: 'Growth', descKey: 'pages.careers.benefit4Desc', desc: 'Rapid career progression in a fast-growing industry' },
  ];

  return (
    <CompanyLayout
      title={t('careers.title', 'Careers')}
      subtitle={t('careers.subtitle', 'Join the team building the future of AI-native trading infrastructure')}
    >
      {/* Hero */}
      <div className={`p-6 md:p-8 rounded-xl border ${borderLight} ${bgCard} mb-8`}>
        <p className={`leading-relaxed mb-4 ${textSecondary}`}>
          We are always looking for exceptional talent to join our mission. At ZhiSu Protocol, you will work alongside some of the brightest minds in crypto, AI, and quantitative finance to build infrastructure that will power the future of global markets.
        </p>
        <div className={`flex items-center gap-6 text-sm ${textMuted}`}>
          <span className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-[#00ff88] animate-pulse" />
            Currently hiring across 8 roles
          </span>
          <span className="flex items-center gap-2">
            <i className="ri-earth-line w-4 h-4 flex items-center justify-center" />
            Remote-first team across 15+ countries
          </span>
        </div>
      </div>

      {/* Benefits */}
      <div className={`p-6 md:p-8 rounded-xl border ${borderLight} ${bgCard} mb-8`}>
        <h2 className={`text-xl font-bold mb-6 ${textPrimary}`}>{t('careers.benefits', 'Why Join ZhiSu')}</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {benefits.map((b) => (
            <div key={b.title} className="flex items-start gap-3">
              <div className="w-9 h-9 rounded-lg bg-[#00ff88]/10 flex items-center justify-center flex-shrink-0">
                <i className={`${b.icon} text-[#00ff88] w-4 h-4 flex items-center justify-center`} />
              </div>
              <div>
                <h3 className={`text-sm font-semibold ${textPrimary}`}>{t(b.titleKey, b.title)}</h3>
                <p className={`text-xs mt-0.5 ${textMuted}`}>{t(b.descKey, b.desc)}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Job Listings */}
      <div>
        <h2 className={`text-xl font-bold mb-4 ${textPrimary}`}>{t('careers.openPositions', 'Open Positions')}</h2>

        {/* Filter tabs */}
        <div className="flex flex-wrap gap-2 mb-6">
          {departmentKeys.map((dept) => (
            <button
              key={dept.key}
              onClick={() => setActiveDept(dept.key)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                activeDept === dept.key
                  ? 'bg-[#00ff88]/10 text-[#00ff88]'
                  : `${isDark ? 'bg-white/5 text-[#888]' : 'bg-black/5 text-[#666]'} hover:bg-[#00ff88]/5`
              }`}
            >
              {t(`pages.careers.${dept.key.toLowerCase().replace(/ & /g, '').replace(/ /g, '')}`, dept.label)}
            </button>
          ))}
        </div>

        {/* Jobs */}
        <div className="space-y-3">
          {filteredJobs.map((job) => (
            <JobCard
              key={job.id}
              job={job}
              isExpanded={expandedJob === job.id}
              onToggle={() => setExpandedJob(expandedJob === job.id ? null : job.id)}
            />
          ))}
        </div>

        {filteredJobs.length === 0 && (
          <p className={`text-center py-12 ${textMuted}`}>
            No open positions in this department at the moment. Check back soon!
          </p>
        )}
      </div>
    </CompanyLayout>
  );
}